import { Inject, Injectable } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { Select } from '@ngxs/store';
import { WINDOW } from '@ng-web-apis/common';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';

import { Guid } from '@fitscovery/common/types';
import { AuthState, User } from '@fitscovery/auth/data-access';
import { PartnerMembershipSubscription, PartnerMembershipSubscriptionState } from '@fitscovery/partner/data-access';
import { MembershipState, Membership, MembershipStatus } from '@fitscovery/membership/data-access';

@Injectable()
export class MembershipButtonService {

  @Select(AuthState.user) userAuth$: Observable<User>;
  @Select(MembershipState.memberships) memberships$: Observable<Membership[]>;
  @Select(PartnerMembershipSubscriptionState.subscriptions) subscriptions$: Observable<PartnerMembershipSubscription[]>;

  private _currentUrl: string;

  constructor(
    @Inject(WINDOW) private window: any,
    private router: Router
  ) {

    this._currentUrl = this.router.url;

    (router.events as any).pipe(
      filter((event: Event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.url),
      tap((url: string) => {
        if (url.includes('memberships')) {
          localStorage.setItem('membershipRedirectUrl', this._currentUrl || '/');
        } else {
          this._currentUrl = url;
        }
      })
    ).subscribe();
  }

  authenticate() {
    const redirectUrl: string = encodeURIComponent(this.window.location.href);
    this.router.navigate(['/auth/authenticate'], { queryParams: { redirectUrl: redirectUrl } });
  }

  get userAndMembership$() {

    const memberships$ = this.memberships$.pipe(
      filter(e => e?.length !== 0),
      map(e => e?.filter(c => c.status !== MembershipStatus.Archived))
    );

    return combineLatest([ this.userAuth$, this.subscriptions$, memberships$ ]).pipe(
      map((data: any) => ({
        userAuth: <User>data[0],
        subscriptions: <PartnerMembershipSubscription[]>data[1],
        memberships: <Membership[]>data[2],
        isAuthenticated: <boolean>data[0],
        hasPendingMembership: <boolean>!!data[2]?.find((e: any) => e.status === MembershipStatus.Pending),
        hasActiveMembership: <boolean>!!data[2]?.find((e: any) => e.status === MembershipStatus.Active),
        hasActiveFreeMembership: data[1]?.length === 0
          && !!data[2]?.find((e: any) => 
            e.status === MembershipStatus.Active
            && `${e.partnerMembershipSubscriptionId}` === Guid.empty
          ),
        hasUpgradeFreeMembership: data[1]?.length > 0 
          && !!data[2]?.find((e: any) => `${e.partnerMembershipSubscriptionId}` === Guid.empty)
      })),
    )
  }

}
